import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { bind } from '../../../utils/decorators';
import { FormControl, Table, Panel, ControlLabel, FormGroup, Col, InputGroup, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { inject } from '../../../utils/decorators';
import { VorgangStore } from '../../../stores/VorgangStore';
import { IArbeitsmittel, IObjektVorlage } from '../../../models/ApiTypes';
import { round } from '../../../utils/round';
import { AgtOpazimeterKalibrierungStore } from '../../../stores/AgtOpazimeterKalibrierungStore';
import FaIcon from '../../site/FaIcon';
import { numberformatFixed } from '../../../utils/numberformat';

export interface IAgtOpazimeterKalibrierungProps {
  filterTyp: number;
  messkammerlaenge: number;
  fehlergrenzeGeraet: number;
  formdataprefix: string;
  value?: string;
  nachjustage: boolean;
}

interface IAgtOpazimeterMesswert {
  soll: number | undefined;
  istEingabe: Array<string | undefined>;
  ist: Array<number | undefined>;
  genauigkeit: number | undefined;
  fehlergrenze: number | undefined;
}

interface IAgtOpazimeterMesswertMitKorrektur extends IAgtOpazimeterMesswert {
  korrigiert: Array<string | undefined>;
}

/**
 * Messwerteingabe für eine Messreihe mti einen definierten Opazimeter Filtertyp
 */
@observer
export default class AgtOpazimeterMessung extends React.Component<IAgtOpazimeterKalibrierungProps, {}> {

    @inject(VorgangStore) vorgangStore: VorgangStore;
    @inject(AgtOpazimeterKalibrierungStore) kalibrierungStore: AgtOpazimeterKalibrierungStore;

    @observable validationstate: 'success' | 'warning' | 'error' | undefined = 'error';

    @observable arbeitsmittel: IArbeitsmittel | undefined = undefined;
    @observable gespeichertesArbeitsmittel: string | undefined;

    @observable truebungsGrad: IAgtOpazimeterMesswert = {
      soll: undefined,
      istEingabe: [undefined, undefined],
      ist: [undefined, undefined],
      genauigkeit: undefined,
      fehlergrenze: undefined
    };

    @observable truebungsKoeffizient: IAgtOpazimeterMesswertMitKorrektur = {
      soll: undefined,
      istEingabe: [undefined, undefined],
      ist: [undefined, undefined],
      korrigiert: [undefined, undefined],
      genauigkeit: undefined,
      fehlergrenze: undefined
    };

    popoverUmrechnungGradKoeffizient = (
      <Popover id="popover-trigger-hover-focus">
        <b>Trübungskoeffizient</b> aus diesem Trübungsgrad berechnen und nach unten übernehmen.
      </Popover>
    );

    popoverUmrechnungKoeffizientGrad = (
      <Popover id="popover-trigger-hover-focus">
        <b>Trübungsgrad</b> aus diesem Trübungskoeffizient berechnen und nach oben übernehmen.
      </Popover>
    );

    @bind @action setTruebungsGrad(index: number, wert: string | undefined) {
      this.truebungsGrad.istEingabe[index] = wert;
      this.truebungsGrad.ist[index] = wert !== undefined ? parseFloat(wert) : undefined;
    }

    @bind @action setTruebungsKoeffizient(index: number, wert: string | undefined) {
      this.truebungsKoeffizient.istEingabe[index] = wert;
      this.truebungsKoeffizient.ist[index] = wert !== undefined ? parseFloat(wert) : undefined;
    }

    // @todo: In obige Funktion integrieren?
    // @todo: Nachkommastellen direkt korrigieren, da keine manuelle Eingabe?
    @bind @action setTruebungsKoeffizientKorrigiert(index: number, wert: number | undefined) {
      this.truebungsKoeffizient.korrigiert[index] = wert !== undefined ? wert.toString() : undefined;
      if (wert !== undefined && this.truebungsKoeffizient.genauigkeit) {
        let corrVal = this.nachkommaKorrektur(wert, this.truebungsKoeffizient.genauigkeit);
        this.truebungsKoeffizient.korrigiert[index] = corrVal;
      }
    }

    // Die Fehlergrenzen für einen Filter, die den Toleranzbereich definieren, hängen von der Klasse des Filters und der Fehlerklasse des Gerätes ab
    @bind @action setFehlergrenzen() {
      // Entsprechend der Tabelle aus der Vorschrift sind die Fehlergrenzen immer gleich, außer beim Filtertyp 0 für Geräte mit Fehlergrenze ±0,1 m^-1
      this.truebungsGrad.fehlergrenze = 5;
      this.truebungsKoeffizient.fehlergrenze = 0.3;
      if (this.props.fehlergrenzeGeraet === 0.1 && this.props.filterTyp === 0) {
        this.truebungsGrad.fehlergrenze = undefined;
        this.truebungsKoeffizient.fehlergrenze = 0.1;
      }
    }

    constructor(props: IAgtOpazimeterKalibrierungProps) {
      super(props);
      // Vorhandene Werte übernehmen
      if (props.value) {
        let obj = JSON.parse(props.value);
        if (obj.truebungsgrad) {
          this.setTruebungsGrad(0, obj.truebungsgrad.messung && obj.truebungsgrad.messung[0]);
          this.setTruebungsGrad(1, obj.truebungsgrad.messung && obj.truebungsgrad.messung[1]);
        }
        if (obj.truebungskoeffizient) {
          this.setTruebungsKoeffizient(0, obj.truebungskoeffizient.messung && obj.truebungskoeffizient.messung[0] && obj.truebungskoeffizient.messung[0].ist);
          this.setTruebungsKoeffizient(1, obj.truebungskoeffizient.messung && obj.truebungskoeffizient.messung[1] && obj.truebungskoeffizient.messung[1].ist);
          // @todo: Direkt in obige Funktion?
          for (let i = 0; i < this.truebungsKoeffizient.ist.length; i++) {
            const koeffizient = this.truebungsKoeffizient.ist[i];
            this.setTruebungsKoeffizientKorrigiert(i, koeffizient ? round(koeffizient * (this.props.messkammerlaenge / 430.0), 3) : undefined);
          }
        }
        if (obj.arbeitsmittel) {
          if (obj.arbeitsmittel) {
            this.setGespeichertesArbeitsmittel(obj.arbeitsmittel.guid);
          }
        }
      }
      this.setFehlergrenzen();
    }

    @computed get titel() {
      let text: string = 'Messung mit Filtertyp ' + this.props.filterTyp.toString();
      if (this.props.nachjustage) {
        text += ' nach Justage';
      }
      return text;
    }

    @computed get formdataName() {
      return `${this.props.formdataprefix}`;
    }

    /**
     * Ermittelt die zum Trübungsgrad gehörende Messunsicherheit für den gewählten Filtertyp anhand der in der Objektvorlage hinterlegten Eigenschaften
     */
    @computed get messunsicherheitTruebungsgrad() {
      let arbeitsmittel: IArbeitsmittel | undefined = this.arbeitsmittel;
      let objektvorlage: IObjektVorlage | undefined = this.kalibrierungStore.objektVorlage;
      if (arbeitsmittel && objektvorlage) {
        for (let meta of objektvorlage.meta) {
          if (meta.kategorie === 'messunsicherheit' && meta.detail === arbeitsmittel.typ.toLocaleLowerCase() && meta.eigenschaft === 'truebungsgrad') {
            return parseFloat(meta.wert);
          }
        }
      }
      return undefined;
    }

    /**
     * Berechnet den Toleranzbereich für den Trübungsgrad, optional unter Einbezug der Messunsicherheit
     */
    @computed get truebungsGradToleranzbereich() {
      let toleranzBereich: number | undefined = undefined;
      for (let i = 0; i < this.truebungsGrad.ist.length; i++) {
        if (this.truebungsGrad.soll && this.truebungsGrad.fehlergrenze) {
            toleranzBereich = round(this.truebungsGrad.fehlergrenze, 3);
            // Messunsicherheit kann optional von Fehlergrenze abgezogen werden
            if (this.kalibrierungStore.messunsicherheitAnwenden && this.messunsicherheitTruebungsgrad) {
              toleranzBereich -= round(this.messunsicherheitTruebungsgrad, 3);
            }
        }
      }
      return toleranzBereich;
    }

    /**
     * Ermittelt ob der anzuwendende Ist-Wert für den Trübungsgrad im über Fehlergrenze und (optional) Messunsucherheit ermitteltem Toleranzbereich liegt
     * @note: Wenn es keine Fehlergrenze für den Trübungsgrad gibt (Geräte mit Fehlergrenze 0,1), keine Toleranzbetrachtung durchführen
     */
    @computed get truebungsGradImToleranzbereich() {
      let toleranzEingehalten: Array<boolean | undefined> = [undefined, undefined];
      for (let i = 0; i < this.truebungsGrad.ist.length; i++) {
        if (this.truebungsGrad.fehlergrenze) {
          const messwert = this.truebungsGrad.ist[i];
          if (messwert !== undefined && this.truebungsGrad.soll !== undefined && this.truebungsGradToleranzbereich) {
            toleranzEingehalten[i] = (messwert >= this.truebungsGrad.soll - this.truebungsGradToleranzbereich) && (messwert <= this.truebungsGrad.soll + this.truebungsGradToleranzbereich);
          }
        } else {
          toleranzEingehalten[i] = true;
        }
      }
      return toleranzEingehalten;
    }

    /**
     * Berechnet den Toleranzbereich für den Trübungkoeffizienten
     */
    @computed get truebungsKoeffizientToleranzbereich() {
      let toleranzBereich: number | undefined = undefined;
      if (this.truebungsKoeffizient.soll && this.truebungsKoeffizient.fehlergrenze) {
        // Fehlergrenze Trübungskoeffizient ist absolut
        toleranzBereich = round(this.truebungsKoeffizient.fehlergrenze, 3);
      }
      return toleranzBereich;
    }

    /**
     * Ermittelt ob der anzuwendende Ist-Wert für den Trübungkoeffizienten im über die Fehlergrenze ermitteltem Toleranzbereich liegt
     */
    @computed get truebungsKoeffizientImToleranzbereich() {
      let toleranzEingehalten: Array<boolean | undefined> = [undefined, undefined];
      for (let i = 0; i < this.truebungsKoeffizient.ist.length; i++) {
        const messwert = this.kalibrierungStore.eingabeKorrigierterKoeffizient ? this.truebungsKoeffizient.ist[i] : this.truebungsKoeffizient.korrigiert[i];
        if (messwert !== undefined && this.truebungsKoeffizient.soll !== undefined && this.truebungsKoeffizientToleranzbereich) {
          toleranzEingehalten[i] =  (messwert >= this.truebungsKoeffizient.soll - this.truebungsKoeffizientToleranzbereich) && (messwert <= this.truebungsKoeffizient.soll + this.truebungsKoeffizientToleranzbereich);
        } else {
          toleranzEingehalten[i] = undefined;
        }
      }
      return toleranzEingehalten;
    }

    private nachkommaStellen(value: string) {
      let nachkommaTeil = value.indexOf('.') > -1 ? value.split('.').pop() : '';
      return nachkommaTeil ? nachkommaTeil.length : 0;
    }

    /**
     * Übernimmt die im aktuell gewählten Filter (Arbeitsmittel) hinterlegten Sollwerte (inkl. Messunsicherheit) aus den Meta-Daten des Arbeitsmittels
     */
    private uebernehmeSollwerte() {
      const { arbeitsmittel } = this;
      if (arbeitsmittel) {
        let l = arbeitsmittel.meta ? arbeitsmittel.meta.length : 0;
        for (let i = 0; i < l; i++) {
          if (arbeitsmittel && arbeitsmittel.meta && arbeitsmittel.meta[i] !== undefined) {
            let metadatum = arbeitsmittel.meta[i];
            if (metadatum.bezeichnung === 'filter_truebungsgrad') {
              this.truebungsGrad.soll = parseFloat(metadatum.inhalt);
              this.truebungsGrad.genauigkeit = this.nachkommaStellen(metadatum.inhalt);
            }
            if (metadatum.bezeichnung === 'filter_truebungskoeffizient') {
              this.truebungsKoeffizient.soll = parseFloat(metadatum.inhalt);
              this.truebungsKoeffizient.genauigkeit = this.nachkommaStellen(metadatum.inhalt);
            }
          }
        }
      } else {
        this.truebungsGrad.soll = undefined;
        this.truebungsKoeffizient.soll = undefined;
        this.truebungsGrad.genauigkeit = undefined;
        this.truebungsKoeffizient.genauigkeit = undefined;
      }
    }

    @action setArbeitsmittel(arbeitsmittel: IArbeitsmittel | undefined) {
      this.arbeitsmittel = arbeitsmittel;
      this.uebernehmeSollwerte();
    }

    @action setGespeichertesArbeitsmittel(arbeitsmittelguid: string | undefined) {
      this.gespeichertesArbeitsmittel = arbeitsmittelguid;
      if (arbeitsmittelguid) {
        // Gespeichertes Arbeitsmittel im aktuellen Vorgang suchen und als Interface in Auswertung übernehmen
        if (this.vorgangStore.vorgang && this.vorgangStore.vorgang.arbeitsmittel) {
          let arbeitsmittel = this.vorgangStore.vorgang.arbeitsmittel.find((am) => (am.arbeitsmittelguid === arbeitsmittelguid));
          if (arbeitsmittel) {
            this.setArbeitsmittel(arbeitsmittel);
          }
        }
      }
    }

    @bind @action handleArbeitsmittelChanged(ev: React.FormEvent<FormControl>) {
      const arbeitsmittelguid = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).value : undefined;
      if (arbeitsmittelguid && this.vorgangStore.vorgang && this.vorgangStore.vorgang.arbeitsmittel) {
        const arbeitsmittel = this.vorgangStore.vorgang.arbeitsmittel.find(am => am.arbeitsmittelguid === arbeitsmittelguid);
        this.setArbeitsmittel(arbeitsmittel);
      } else {
        this.setArbeitsmittel(undefined);
      }
    }

    @bind @action handleKoeffizientChange(index: number, ev: React.FormEvent<FormControl>) {
      // @todo: rework, Text und Regex
      this.setTruebungsKoeffizient(index, ((ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).value : undefined));
      // Der korrigierte Trübungskoeffizient wird über die im AGT hinterlegte Messskammerlänge ermittelt
      // @todo: in obige Funktione
      const koeffizient = this.truebungsKoeffizient.ist[index];
      this.setTruebungsKoeffizientKorrigiert(index, koeffizient ? round(koeffizient * (this.props.messkammerlaenge / 430.0), 3) : undefined);
    }

    @bind @action handleGradChange(index: number, ev: React.FormEvent<FormControl>) {
      // @todo: rework, Text und Regex
      this.setTruebungsGrad(index, ((ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).value : undefined));
    }

    /**
     * @todo: comment
     */
    private nachkommaKorrektur(wert: number, nachkommastellen: number) {
      const strVal = wert.toString();
      const trenner = '.';
      let tstVal = strVal;
      let nachkommaTeil = strVal.indexOf(trenner) > -1 ? strVal.split(trenner).pop() : '';
      let nachkommaLen = nachkommaTeil ? nachkommaTeil.length : 0;
      if (nachkommaLen < nachkommastellen) {
        if (strVal.indexOf(trenner) === -1) {
          tstVal += trenner;
        }
        tstVal = tstVal + '0'.repeat(nachkommastellen - nachkommaLen);
      }
      if (tstVal && nachkommaLen > nachkommastellen) {
        tstVal = round(parseFloat(tstVal), nachkommastellen).toString();
      }
      return tstVal;
    }

    /**
     * Der Event Handler für das Verlassen des Eingabefeldes Istwert kümmert sich um die Verlängerung oder Verkürzung
     * des Ist-Wertes entsprechend der im Sollwert definierten Nachkommastellen
     */
    @bind @action handleGradBlur(index: number, ev: React.FormEvent<FormControl>) {
      let val = this.truebungsGrad.ist[index];
      if (val !== undefined && this.truebungsGrad.genauigkeit !== undefined) {
        let corrVal = this.nachkommaKorrektur(val, this.truebungsGrad.genauigkeit);
        this.setTruebungsGrad(index, corrVal);
      }
    }

    @bind @action handleKoeffizientBlur(index: number, ev: React.FormEvent<FormControl>) {
      let val = this.truebungsKoeffizient.ist[index];
      if (val !== undefined && this.truebungsKoeffizient.genauigkeit !== undefined) {
        let corrVal = this.nachkommaKorrektur(val, this.truebungsKoeffizient.genauigkeit);
        this.setTruebungsKoeffizient(index, corrVal);
        const koeffizient = this.truebungsKoeffizient.ist[index];
        this.setTruebungsKoeffizientKorrigiert(index, koeffizient ? round(koeffizient * (this.props.messkammerlaenge / 430.0), 3) : undefined);
      }
    }

    /**
     * Rechnet den Trübungsgrad anhand der PTB-Formel in den Trübungskoeffizienten um
     */
    @bind @action berechneKoeffizientAusTruebungsgrad(index: number) {
      if (!this.truebungsGrad.ist[index]) {
        return;
      }
      if (this.truebungsGrad.ist && this.truebungsGrad.ist[index]) {
        let N = this.truebungsGrad.ist[index] !== undefined ? this.truebungsGrad.ist[index] : undefined;
        if (N) {
          // Wenn das Gerät bereits korrigierte Koeffizienten anzeigt immer auf 430mm als Messkammerlänge beziehen
          let mk = (this.kalibrierungStore.eingabeKorrigierterKoeffizient ? 430.0 : this.props.messkammerlaenge) * 0.001;
          let k = -Math.log(1.0 - (N / 100.0)) * (1.0 / mk);
          // this.truebungskoeffizient[index] = round(k, 3);
          // @todo: über setter
          this.setTruebungsKoeffizientKorrigiert(index, round(k * (this.props.messkammerlaenge / 430.0), 3));

          // @todo: K korrigiert
          let corrVal = this.nachkommaKorrektur(k, this.truebungsKoeffizient.genauigkeit ? this.truebungsKoeffizient.genauigkeit : 3);
          this.setTruebungsKoeffizient(index, corrVal);
        }
      }
    }

    /**
     * Rechnet den Trübungskoeffizienten anhand der umgestellten PTB-Formel in den Trübungsgrad um
     */
    @bind @action berechneTruebungsgradAusKoeffizient(index: number) {
      if (!this.truebungsKoeffizient.ist[index]) {
        return;
      }
      if (this.truebungsKoeffizient.ist[index]) {
        let k = this.truebungsKoeffizient.ist[index] !== undefined ? this.truebungsKoeffizient.ist[index] : undefined;
        if (k) {
          // Wenn das Gerät bereits korrigierte Koeffizienten anzeigt immer auf 430mm als Messkammerlänge beziehen
          let mk = (this.kalibrierungStore.eingabeKorrigierterKoeffizient ? 430.0 : this.props.messkammerlaenge) * 0.001;
          let fmk = 1.0 / mk;
          let N = -(Math.exp(-(k / fmk)) - 1.0) * 100.0;
          // this.truebungsGrad.ist[index] = round(N, 3);
          let corrVal = this.nachkommaKorrektur(N, this.truebungsGrad.genauigkeit ? this.truebungsGrad.genauigkeit : 3);
          this.setTruebungsGrad(index, corrVal);
        }
      }
    }

    public renderArbeitsmittelAuswahl() {
      let arbeitsmittel: IArbeitsmittel[] = [];
      // Zur Art des Prüfgases der Komponente passenden Arbeitsmittel aus aktuell gewähltem Set ermitteln
      if (this.vorgangStore.vorgang && this.vorgangStore.vorgang.arbeitsmittel) {
        arbeitsmittel = this.vorgangStore.vorgang.arbeitsmittel.filter(am => {
          if (am.typ === 'AGT_FILTER_' + this.props.filterTyp.toString()) {
            return true;
          }
          return false;
        });
        return (
          <div>
            <FormGroup validationState={this.arbeitsmittel !== undefined ? undefined : 'error'} controlId="Verwendeter Filter">
              <Col componentClass={ControlLabel} sm={4}>Verwendeter Filter</Col>
              <Col sm={8}>
                {!this.kalibrierungStore.readOnly ?
                  <FormControl
                    componentClass="select"
                    placeholder="select"
                    onChange={this.handleArbeitsmittelChanged}
                    defaultValue={this.gespeichertesArbeitsmittel}
                    name={`${this.formdataName}[arbeitsmittel][guid]`}
                  >
                    <option value={undefined}/>
                    {arbeitsmittel.map(am => <option key={am.arbeitsmittelguid} value={am.arbeitsmittelguid}> {am.name} ({am.arbeitsmittelnr})</option>)}
                  </FormControl>
                  :
                  <FormControl
                    type="text"
                    value={this.arbeitsmittel && `${this.arbeitsmittel.name} (${this.arbeitsmittel.arbeitsmittelnr})`}
                    readOnly={true}
                    disabled={true}
                  />
                }
                <FormControl type="hidden" name={`${this.formdataName}[arbeitsmittel][nr]`} value={this.arbeitsmittel ? this.arbeitsmittel.arbeitsmittelnr : ''}/>
                <FormControl type="hidden" name={`${this.formdataName}[arbeitsmittel][name]`} value={this.arbeitsmittel ? this.arbeitsmittel.name : ''}/>
                <FormControl type="hidden" name={`${this.formdataName}[arbeitsmittel][typ]`} value={this.arbeitsmittel ? this.arbeitsmittel.typ : ''}/>
                <FormControl type="hidden" name={`${this.formdataName}[arbeitsmittel][messunsicherheit][truebungsgrad]`} value={this.messunsicherheitTruebungsgrad ? this.messunsicherheitTruebungsgrad : ''}/>
              </Col>
            </FormGroup>
          </div>
        );
      }
      return (<div>Keine Arbeitsmittel im Vorgang hinterlegt!!!</div>);
    }

    private renderToleranzBereich(wert: number, bereich: number) {
      return (
        <InputGroup>
          <InputGroup.Addon>±</InputGroup.Addon>
          <FormControl
            type="number"
            readOnly={true}
            tabIndex={-1}
            value={round(bereich, 3)}
          />
        </InputGroup>
      );
    }

    private renderTruebungsGrad() {
      return (
        <tr>
          <td className="komponente">Grad</td>
          <td>
            <InputGroup>
              <FormControl
                type="text"
                readOnly={true}
                tabIndex={-1}
                name={`${this.props.formdataprefix}[truebungsgrad][soll]`}
                value={numberformatFixed(this.truebungsGrad.soll, this.truebungsGrad.genauigkeit ? this.truebungsGrad.genauigkeit : 3)}
              />
              <InputGroup.Addon className="komponenteEinheit">%</InputGroup.Addon>
            </InputGroup>
          </td>
          {/* Eingabe */}
          {[0, 1].map((index) => (
            <td key={index}>
              <InputGroup>
                <FormControl
                  type="number"
                  step="any"
                  name={`${this.props.formdataprefix}[truebungsgrad][messung][${index}]`}
                  onChange={(e) => this.handleGradChange(index, e)}
                  onBlur={(e) => this.handleGradBlur(index, e)}
                  readOnly={this.kalibrierungStore.readOnly}
                  value={this.truebungsGrad.istEingabe[index] !== undefined ? this.truebungsGrad.istEingabe[index] : ''}
                  className={this.truebungsGradImToleranzbereich[index] === true ? 'text-success' : 'text-danger'}
                  autoComplete="off"
                />
                <OverlayTrigger trigger={['hover', 'focus', 'click']} placement="top" overlay={this.popoverUmrechnungGradKoeffizient}>
                  <InputGroup.Button style={{width: '28px'}}>
                    <Button
                      bsStyle="default"
                      className="btn-group"
                      disabled={this.kalibrierungStore.readOnly}
                      onClick={() => this.berechneKoeffizientAusTruebungsgrad(index)}>
                      <FaIcon icon="level-down" />
                    </Button>
                  </InputGroup.Button>
                </OverlayTrigger>
                <FormControl
                  type="hidden"
                  name={`${this.props.formdataprefix}[truebungsgrad][fehlergrenze][${index}]`}
                  value={this.truebungsGradImToleranzbereich[index] === true ? 'true' : 'false'}
                />
              </InputGroup>
              <FormControl type="hidden" name={`${this.props.formdataprefix}[truebungsgrad][genauigkeit]`} value={this.truebungsGrad.genauigkeit} />
            </td>
          ))}
          {/* Toleranzbereich */}
          <td>
            {this.truebungsGradToleranzbereich !== undefined && this.truebungsGrad.soll &&
              this.renderToleranzBereich(this.truebungsGrad.soll, this.truebungsGradToleranzbereich)
            }
          </td>
        </tr>
      );
    }

    public render() {
      return(
        <Panel header={this.titel} collapsible={true} defaultExpanded={true}>
          {this.renderArbeitsmittelAuswahl()}
          <Table striped={false} bordered={false} condensed={true} hover={false} className="agt-auswertung">
            <thead>
              <tr>
                  <th/>
                  <th>Filterwert</th>
                  <th>Messung 1</th>
                  <th>Messung 2</th>
                  <th>Toleranzbereich</th>
              </tr>
            </thead>
            <tbody>
              {this.renderTruebungsGrad()}
              {/* Trübungskoeffizient */}
              <tr>
                <td className="komponente">Koeffizient</td>
                <td>
                  <InputGroup>
                    <FormControl
                      type="number"
                      readOnly={true}
                      tabIndex={-1}
                      name={`${this.props.formdataprefix}[truebungskoeffizient][soll]`}
                      value={this.truebungsKoeffizient.soll}
                    />
                    <InputGroup.Addon className="komponenteEinheit">m<sup>-1</sup></InputGroup.Addon>
                  </InputGroup>
                </td>
                {/* Eingabe */}
                {[0, 1].map((index) => (
                  <td key={index}>
                    <InputGroup>
                      <FormControl
                        type="number"
                        step="any"
                        name={`${this.props.formdataprefix}[truebungskoeffizient][messung][${index}][ist]`}
                        onChange={(e) => this.handleKoeffizientChange(index, e)}
                        onBlur={(e) => this.handleKoeffizientBlur(index, e)}
                        readOnly={this.kalibrierungStore.readOnly}
                        value={this.truebungsKoeffizient.istEingabe[index] !== undefined ? this.truebungsKoeffizient.istEingabe[index] : ''}
                        className={this.kalibrierungStore.eingabeKorrigierterKoeffizient ? (this.truebungsKoeffizientImToleranzbereich[index] === true ? 'text-success' : 'text-danger') : undefined}
                      />
                      <OverlayTrigger trigger={['hover', 'focus', 'click']} placement="bottom" overlay={this.popoverUmrechnungKoeffizientGrad}>
                        <InputGroup.Button style={{width: '28px'}}>
                          <Button
                            bsStyle="default"
                            className="btn-group"
                            disabled={this.kalibrierungStore.readOnly}
                            onClick={() => this.berechneTruebungsgradAusKoeffizient(index)}>
                            <FaIcon icon="level-up" />
                          </Button>
                        </InputGroup.Button>
                      </OverlayTrigger>
                      <FormControl
                        type="hidden"
                        name={`${this.props.formdataprefix}[truebungskoeffizient][messung][${index}][korrigiert]`}
                        value={this.truebungsKoeffizient.korrigiert[index]}
                      />
                      <FormControl
                        type="hidden"
                        name={`${this.props.formdataprefix}[truebungskoeffizient][fehlergrenze][${index}]`}
                        value={this.truebungsKoeffizientImToleranzbereich[index] === true ? 'true' : 'false'}
                      />
                    </InputGroup>
                    <FormControl type="hidden" name={`${this.props.formdataprefix}[truebungskoeffizient][genauigkeit]`} value={this.truebungsKoeffizient.genauigkeit} />
                  </td>
                ))}
                {/* Toleranzbereich */}
                <td>
                  {this.truebungsKoeffizientToleranzbereich && this.truebungsKoeffizient.soll &&
                    this.renderToleranzBereich(this.truebungsKoeffizient.soll, this.truebungsKoeffizientToleranzbereich)
                  }
                </td>
              </tr>
              {/* Korrigierter Trübungskoeffizient */}
              {!this.kalibrierungStore.eingabeKorrigierterKoeffizient &&
                <tr>
                  <td/>
                  <td className="eingabeCaption">Korrigiert</td>
                  {[0, 1].map((index) => (
                    <td key={index}>
                      <InputGroup>
                        <OverlayTrigger trigger={['hover', 'focus', 'click']} placement="bottom" overlay={<Popover id="popover-trigger-hover-focus">Für den Abgleich gegen den Filterwert wird der über die Messkammerlänge korrigierte Koeffizient verwendet</Popover>}>
                          <FormControl
                            type="number"
                            readOnly={true}
                            tabIndex={-1}
                            value={this.truebungsKoeffizient.korrigiert[index] !== undefined ? this.truebungsKoeffizient.korrigiert[index] : ''}
                            className={this.truebungsKoeffizientImToleranzbereich[index] === true ? 'text-success' : 'text-danger'}
                            style={{border: '0px', backgroundColor: 'inherit'}}
                          />
                        </OverlayTrigger>
                      </InputGroup>
                    </td>
                  ))}
                  <td/>
                </tr>
              }
            </tbody>
          </Table>
          {/* Fehlergrenzen für diesen Filtertyp unsichtbar im Request übergeben */}
          <FormControl type="hidden" name={`${this.props.formdataprefix}[fehlergrenzen][truebungsgrad]`} value={this.truebungsGrad.fehlergrenze} />
          <FormControl type="hidden" name={`${this.props.formdataprefix}[fehlergrenzen][truebungskoeffizient]`} value={this.truebungsKoeffizient.fehlergrenze} />
        </Panel>
      );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/agt/AgtOpazimeterMessung.tsx